/*======================================================================================================================
  * Bluebell: src/app/state/results/results.effects.ts
  * Last Edited: 05/09/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { inject, Injectable } from '@angular/core';
import { ResultsService } from '@app/pages/results/results.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { selectResultsBatchSize } from '@state/account/account.selectors';
import { ApiService } from '@state/api.service';
import { selectCurrentProgrammeId } from '@state/programmes/programme.selectors';
import { ProjectActions } from '@state/projects/project.actions';
import { selectCurrentProjectId } from '@state/projects/project.selectors';
import { RollingReport } from '@state/reports/reports.interfaces';
import { selectCurrentReport } from '@state/reports/reports.selectors';
import {
  selectReportView,
  setCurrentView,
  setCurrentViewDeltaBaseline,
} from '@state/results-view/results-view.actions';
import { ResultsView } from '@state/results-view/results-view.interfaces';
import { selectReportsResultsView } from '@state/results-view/results-view.selectors';
import {
  calculationParseComplete, clearDatedResults,
  createCalculation,
  createCalculationFailure,
  createCalculationSuccess,
  createTextComment,
  createTextCommentFailure,
  createTextCommentSuccess,
  deleteCalculation,
  deleteCalculationFailure,
  deleteCalculationSuccess,
  deleteTextComment,
  deleteTextCommentFailure,
  deleteTextCommentSuccess,
  deleteWakeGridWeighting,
  deleteWakeGridWeightingFailure,
  deleteWakeGridWeightingSuccess,
  deselectAllResults,
  evaluateCalculations,
  evaluateCalculationsFailure,
  evaluateCalculationsRun,
  evaluateCalculationsRunFailure,
  evaluateCalculationsRunSuccess,
  evaluateCalculationsSuccess,
  getWakeGridsWeightings,
  getWakeGridsWeightingsFail,
  getWakeGridsWeightingsSuccess,
  listDownloadPostproAvailable,
  listDownloadPostproAvailableFailure,
  listDownloadPostproAvailableSuccess,
  loadReportResults,
  loadReportResultsFailure,
  loadReportResultsSuccess,
  loadResultsBatch,
  loadResultsBatchFailure,
  loadResultsBatchSuccess, loadResultsByDate, loadResultsByDateFailure,
  loadResultsByDateSuccess,
  loadSandboxResults,
  loadSandboxResultsFailure,
  loadSandboxResultsSuccess,
  reloadAllResults,
  reloadAllResultsFailure,
  reloadAllResultsSuccess,
  reprocessCalculation,
  resultsComponentLoad,
  resultsComponentRefresh,
  resultsProjectChanged,
  retrieveCalculations,
  retrieveCalculationsFailure,
  retrieveCalculationsSuccess,
  saveWakeGridWeightings,
  saveWakeGridWeightingsFail,
  saveWakeGridWeightingsSuccess,
  setTextColumnApproval,
  setTextColumnApprovalFailure,
  setTextColumnApprovalSuccess,
  updateCalculation,
  updateCalculationFailure,
  updateCalculationSuccess,
  updateResultColors,
  updateResultColorsFailure,
  updateResultColorsSuccess,
  updateResultComment,
  updateResultCommentFailure,
  updateResultCommentSuccess,
  updateResultDescription,
  updateResultDescriptionFailure,
  updateResultDescriptionSuccess,
  updateTextComment,
  updateTextCommentFailure,
  updateTextCommentSuccess,
} from '@state/results/results.actions';
import {
  CalcResult,
  Calculation,
  CaseDownloadPostproAvailable,
  defaultLimitOffset,
  ListResultsResponse,
  Result,
  TextColumnComment,
  WakeGridWeightsTemplate,
} from '@state/results/results.interfaces';
import { selectResultsDateParameters, selectResultsLimit, selectResultsOffset } from '@state/results/results.selectors';
import { deselectSandbox } from '@state/sandbox/sandbox.actions';
import { selectSelectedSandbox, selectSelectedSandboxId } from '@state/sandbox/sandbox.selectors';
import { MessageService } from 'primeng/api';
import { concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';


@Injectable()
export class ResultsEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private resultsService = inject(ResultsService);
  private messageService = inject(MessageService);
  private handler = inject(EffectHandlerService);

  limit = this.store.selectSignal(selectResultsLimit);
  offset = this.store.selectSignal(selectResultsOffset);

  // Load a set batch of results between a set limit and offset
  loadBatch$ = createEffect(() => this.actions$.pipe(
    ofType(loadResultsBatch),
    concatMap(({ limit, offset }) => this.resultsService.loadProjectResults(offset, limit).pipe(
      this.handler.effectResponse({
        retry: loadResultsBatch({ limit, offset }),
        success: (response: ListResultsResponse) => loadResultsBatchSuccess({ response, limit, offset }),
        failure: loadResultsBatchFailure(),
        failureMessage: 'Could not load results'
      })
    ))
  ));

  createCalculation$ = createEffect(() => this.actions$.pipe(
    ofType(createCalculation),
    mergeMap(({ calculation, old_uuid }) => this.api.createCalculation(calculation, old_uuid).pipe(
      this.handler.effectResponse({
        success: (response: Calculation) => createCalculationSuccess({ calculation: response }),
        successMessage: 'Calculation created successfully',
        retry: createCalculation({ calculation, old_uuid }),
        failure: createCalculationFailure(),
        failureMessage: 'Could not create calculation'
      })
    ))
  ));

  retrieveCalculations$ = createEffect(() => this.actions$.pipe(
    ofType(retrieveCalculations),
    concatLatestFrom(() => this.store.select(selectCurrentProjectId)),
    filter((payload): payload is [ReturnType<typeof retrieveCalculations>, string] => !!payload[1]),
    switchMap(([, project]) => this.api.listCalculations(project as string).pipe(
      this.handler.effectResponse({
        success: (calculations: Calculation[]) => retrieveCalculationsSuccess({ calculations }),
        retry: retrieveCalculations(),
        failure: retrieveCalculationsFailure(),
        failureMessage: 'Could not load calculations'
      })
  ))
  ));

  updateCalculation$ = createEffect(() => this.actions$.pipe(
    ofType(updateCalculation),
    switchMap(({ calculation }) => this.api.updateCalculation(calculation).pipe(
      this.handler.effectResponse({
        success: (response: Calculation) => updateCalculationSuccess({ calculation: response }),
        retry: updateCalculation({ calculation }),
        failure: updateCalculationFailure(),
        failureMessage: 'Could not update calculation'
      })
    ))
  ));

  deleteCalculation$ = createEffect(() => this.actions$.pipe(
    ofType(deleteCalculation),
    switchMap(({ uuid }) => this.api.deleteCalculation(uuid).pipe(
      this.handler.effectResponse({
        success: () => deleteCalculationSuccess({ uuid }),
        successMessage: 'Calculation deleted successfully',
        retry: deleteCalculation({ uuid }),
        failure: deleteCalculationFailure(),
        failureMessage: 'Could not delete calculation'
      })
    ))
  ));

  evaluateCalculations$ = createEffect(() => this.actions$.pipe(
    ofType(evaluateCalculations),
    switchMap(({ cases, equations, baseline }) => {
      return this.api.evaluateCalculations(cases, equations, baseline).pipe(
        this.handler.effectResponse({
          success: (deltaResults: CalcResult) => evaluateCalculationsSuccess({ deltaResults }),
          successMessage: 'Calculation evaluated successfully',
          retry: evaluateCalculations({ cases, equations, baseline }),
          failure: evaluateCalculationsFailure(),
          failureMessage: 'Calculation evaluation failed'
        })
      );
    })
  ));

  evaluateCalculationsRun$ = createEffect(() => this.actions$.pipe(
    ofType(evaluateCalculationsRun),
    switchMap(({ cases, equations, baseline_run }) => {
      return this.api.evaluateCalculationsRun(cases, equations, baseline_run).pipe(
        this.handler.effectResponse({
          success: (deltaResults: CalcResult) => evaluateCalculationsRunSuccess({ deltaResults }),
          successMessage: 'Run calculation evaluated successfully',
          retry: evaluateCalculationsRun({ cases, equations, baseline_run }),
          failure: evaluateCalculationsRunFailure(),
          failureMessage: 'Run calculation evaluation failed'
        })
      );
    })
  ));

  projectChangeResetDeltaBaseline$ = createEffect(() => this.actions$.pipe(
    ofType(resultsProjectChanged),
    map(() => setCurrentViewDeltaBaseline({ deltaBaseline: null }))
  ));

  projectChangeReloadCalculations$ = createEffect(() => this.actions$.pipe(
    ofType(resultsProjectChanged),
    map(() => retrieveCalculations())
  ));

  // TODO: Temporarily hiding the message to assess need
  // projectChangeCheckCalculations$ = createEffect(() => this.actions$.pipe(
  //   ofType(retrieveCalculationsSuccess),
  //   concatLatestFrom(() => [
  //     this.store.select(selectCurrentViewColumnConfigs),
  //     this.store.select(selectCalculations),
  //   ]),
  //   map(([, columnConfigs, calculations]: [Action, ResultsColumnConfig[] | null, Calculation[]]) => {
  //     const calcCols: string[] = (columnConfigs || []).filter((config: ResultsColumnConfig) => config.calculation)
  //       .map((conf: ResultsColumnConfig) => conf.property);
  //     const projectCalculations: string[] = calculations.map((calc: Calculation) => calc.name);
  //     if (calcCols.length && calcCols.some(col => !projectCalculations.includes(col))) {
  //       this.messageService.add({
  //         severity: 'warn',
  //         summary: 'Incompatible Calculation Detected',
  //         detail: 'Calculations are tied to a single project. Please ensure that'
  //                 + ' the calculation exist in the current project, or re-import'
  //                 + ' the calculation from the original project if required.',
  //         life: 8000,
  //       });
  //     }
  //   })
  // ), {dispatch: false});

  // Reload all results up to the current limit with offset of 0
  reloadAll$ = createEffect(() => this.actions$.pipe(
    ofType(reloadAllResults),
    concatLatestFrom(() => [
      this.store.select(selectResultsLimit),
      this.store.select(selectResultsDateParameters),
      this.store.select(selectCurrentProgrammeId)
    ]),
    switchMap(([, limit, dateParams, programmeUUID]) => {
      if (dateParams && programmeUUID) {
        return this.resultsService.loadResultsByDate(dateParams, programmeUUID as string).pipe(
          this.handler.effectResponse({
            retry: loadResultsByDate({ params: dateParams}),
            success: (response: Result[]) => loadResultsByDateSuccess({ results: response }),
            failure: loadResultsByDateFailure(),
            failureMessage: 'Could not load results'
          })
        );
      } else {
        return this.resultsService.loadProjectResults(0, limit).pipe(
          this.handler.effectResponse({
            retry: reloadAllResults(),
            success: (response: ListResultsResponse) => reloadAllResultsSuccess({ response }),
            failure: reloadAllResultsFailure(),
            failureMessage: 'Could not load results'
          })
        )
      }
    })
  ));

  // Reload all results when the current view changes
  onCurrentViewUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(setCurrentView),
    concatLatestFrom(() => this.store.select(selectSelectedSandboxId)),
    map(([, sandboxSelected]) => {
      if (sandboxSelected) {
        return loadSandboxResults({ uuid: sandboxSelected });
      } else {
        return reloadAllResults();
      }
    }),
  ));

  loadSandboxResults$ = createEffect(() => this.actions$.pipe(
    ofType(loadSandboxResults),
    switchMap(({ uuid }) => this.resultsService.loadSandboxResults(uuid).pipe(
      this.handler.effectResponse({
        retry: loadSandboxResults({ uuid }),
        success: (response: Result[]) => loadSandboxResultsSuccess({ results: response }),
        failure: loadSandboxResultsFailure(),
        failureMessage: 'Could not load results'
      })
    ))
  ));

  loadResultsByDate$ = createEffect(() => this.actions$.pipe(
    ofType(loadResultsByDate),
    concatLatestFrom(() => this.store.select(selectCurrentProgrammeId)),
    filter((payload) => payload[1] != null),
    switchMap(([{ params, message }, programmeUUID]) =>
      this.resultsService.loadResultsByDate(params, programmeUUID as string).pipe(
        this.handler.effectResponse({
          retry: loadResultsByDate({ params, message }),
          success: (response: Result[]) => loadResultsByDateSuccess({ results: response }),
          failure: loadResultsByDateFailure(),
          failureMessage: 'Could not load results'
        })
      ))
  ));

  /**
   * Download Postpro
   */

  getAvailableDownloadPostpro$ = createEffect(() => this.actions$.pipe(
    ofType(listDownloadPostproAvailable),
    switchMap(({ cases, includeUnapproved }) => this.api.listDownloadPostproAvailable(cases, includeUnapproved).pipe(
      this.handler.effectResponse({
        retry: listDownloadPostproAvailable({ cases, includeUnapproved }),
        success: (availableDownloadPostpro: CaseDownloadPostproAvailable[]) =>
          listDownloadPostproAvailableSuccess({ availableDownloadPostpro }),
        failure: listDownloadPostproAvailableFailure(),
        failureMessage: 'Error getting available postpro'
      })
    ))
  ));

  /**
   * Update Row Colors
   */

  updateRowColors$ = createEffect(() => this.actions$.pipe(
    ofType(updateResultColors),
    mergeMap(({ uuids, force_color }) => this.api.updateColorCases(uuids, force_color).pipe(
      this.handler.effectResponse({
        retry: updateResultColors({ uuids, force_color }),
        success: () => updateResultColorsSuccess({ uuids, force_color }),
        failure: updateResultColorsFailure(),
        failureMessage: 'Error updating row colors',
        successMessage: 'Row colors updated successfully',
      })
    ))
  ));

  /**
   * Component-Based Effects
   */

  // Initial Batch load of 5 runs
  loadResultsComponent$ = createEffect(() => this.actions$.pipe(
    ofType(resultsComponentLoad, resultsProjectChanged, deselectSandbox),
    concatLatestFrom(() => [
      this.store.select(selectSelectedSandbox),
      this.store.select(selectResultsBatchSize),
    ]),
    filter(([, sandbox]) => !sandbox),
    map(([, , batchSize]) => {
      return loadResultsBatch({ offset: 0, limit: batchSize || defaultLimitOffset.limit });
    })
  ));

  // // Initial Secondary Batch load of 455 runs
  // loadResultsComponentSecondary$ = createEffect(() => this.actions$.pipe(
  //   ofType(resultsComponentLoad, resultsProjectChanged, deselectSandbox),
  //   concatLatestFrom(() => this.store.select(selectSelectedSandbox)),
  //   filter(([_, sandbox]) => !sandbox),
  //   map(() => loadResultsBatch({ limit: 15, offset: 5 }))
  // ));

  // Reload all results when the page is refreshed, or when calculations are finished parsing
  refreshResultsComponent$ = createEffect(() => this.actions$.pipe(
    ofType(resultsComponentRefresh, deselectSandbox, calculationParseComplete, clearDatedResults),
    concatLatestFrom(() => this.store.select(selectSelectedSandboxId)),
    map(([, sandboxSelected]) => {
      if (sandboxSelected) {
        return loadSandboxResults({ uuid: sandboxSelected });
      } else {
        // TODO: May need to retrieve calculation list here if parse was not
        //  initiated by the user.
        return reloadAllResults();
      }
    })
  ));

  // // Rest the limit and offset when the project is changed
  // projectChangeResetLimitOffset$ = createEffect(() => this.actions$.pipe(
  //   ofType(resultsProjectChanged),
  //   map(() => setLimitOffset(defaultLimitOffset))
  // ));

  loadReportResultsOnViewSelect$ = createEffect(() => this.actions$.pipe(
    ofType(selectReportView),
    map(() => loadReportResults()),
  ));

  loadReportResults$ = createEffect(() => this.actions$.pipe(
    ofType(loadReportResults),
    concatLatestFrom(() => [
      this.store.select(selectCurrentReport),
      this.store.select(selectReportsResultsView),
    ]),
    filter((payload: [Action, RollingReport|null, ResultsView|null]): payload is [Action, RollingReport, ResultsView] => !!payload[1]),
    switchMap(([, report, view]: [Action, RollingReport, ResultsView]) => this.resultsService.loadReportResults(report.uuid as string, view).pipe(
      this.handler.effectResponse({
        retry: loadReportResults(),
        success: (response: Result[]) => loadReportResultsSuccess({ reportResults: response }),
        failure: loadReportResultsFailure(),
        failureMessage: 'Could not load results'
      })
    )),
  ));

  getWakeWeights$ = createEffect(() => this.actions$.pipe(
    ofType(getWakeGridsWeightings),
    switchMap(() => {
      return this.api.listWakeWeights().pipe(
        this.handler.effectResponse({
          retry: getWakeGridsWeightings(),
          success: (weightings: WakeGridWeightsTemplate[]) => getWakeGridsWeightingsSuccess({ weightings }),
          failure: getWakeGridsWeightingsFail(),
          failureMessage: 'Could not load wake grid weightings'
        })
      );
    })
  ));

  deleteWakeWeight$ = createEffect(() => this.actions$.pipe(
    ofType(deleteWakeGridWeighting),
    switchMap(({ uuid }) => this.api.deleteWakeWeight(uuid).pipe(
      this.handler.effectResponse({
        retry: deleteWakeGridWeighting({ uuid }),
        success: () => deleteWakeGridWeightingSuccess({ uuid }),
        failure: deleteWakeGridWeightingFailure(),
        failureMessage: 'Could not delete wake grid weighting'
      })
    ))
  ));

  saveWakeWeights$ = createEffect(() => this.actions$.pipe(
    ofType(saveWakeGridWeightings),
    switchMap(({ weighting }) => (
        weighting.uuid
          ? this.api.updateWakeWeight(weighting)
          : this.api.createWakeWeight(weighting)
      ).pipe(
        this.handler.effectResponse({
          retry: saveWakeGridWeightings({ weighting }),
          success: (weightingResponse: WakeGridWeightsTemplate) => saveWakeGridWeightingsSuccess({ weighting: weightingResponse }),
          failure: saveWakeGridWeightingsFail(),
          failureMessage: 'Could not save wake grid weighting'
        })
      )
    )
  ));

  updateResultComment$ = createEffect(() => this.actions$.pipe(
    ofType(updateResultComment),
    switchMap(({ caseUUID, comment, runUUID }) => this.api.caseSetComment(caseUUID, comment).pipe(
      this.handler.effectResponse({
        retry: updateResultComment({ caseUUID, comment, runUUID }),
        success: () => updateResultCommentSuccess({ caseUUID, comment, runUUID }),
        failure: updateResultCommentFailure(),
        failureMessage: 'Could not update result comment'
      })
    ))
  ));

  updateResultDesc$ = createEffect(() => this.actions$.pipe(
    ofType(updateResultDescription),
    switchMap(({ uuid, description }) => this.api.updateRun({ uuid, description }).pipe(
      this.handler.effectResponse({
        retry: updateResultDescription({ uuid, description }),
        success: () => updateResultDescriptionSuccess({ uuid, description }),
        failure: updateResultDescriptionFailure(),
        failureMessage: 'Could not update result description'
      })
    ))
  ));

  createTextComment$ = createEffect(() => this.actions$.pipe(
    ofType(createTextComment),
    mergeMap(({ comment, column_uuid, case_uuid, pre_approve }) =>
      this.api.createTextColumnComment(comment, column_uuid, case_uuid, pre_approve).pipe(
        this.handler.effectResponse({
          retry: createTextComment({ comment, column_uuid, case_uuid, pre_approve }),
          success: (textComment: TextColumnComment) => createTextCommentSuccess({ textComment }),
          failure: createTextCommentFailure(),
          failureMessage: 'Could not create text comment'
        })
      ))
  ));

  updateTextComment$ = createEffect(() => this.actions$.pipe(
    ofType(updateTextComment),
    switchMap(({ uuid, comment }) =>
      this.api.updateTextColumnComment(uuid, comment).pipe(
        this.handler.effectResponse({
          retry: updateTextComment({ uuid, comment }),
          success: (textComment: TextColumnComment) => updateTextCommentSuccess({ textComment }),
          failure: updateTextCommentFailure(),
          failureMessage: 'Could not update text comment'
        })
      ))
  ));

  setTextColumnApproval$ = createEffect(() => this.actions$.pipe(
    ofType(setTextColumnApproval),
    switchMap(({ uuid, approved, comment }) =>
      this.api.setTextColumnApproval(uuid, approved, comment).pipe(
        this.handler.effectResponse({
          retry: setTextColumnApproval({ uuid, approved, comment }),
          success: (textComment: TextColumnComment) => setTextColumnApprovalSuccess({ textComment }),
          failure: setTextColumnApprovalFailure(),
          successMessage: `Text column ${approved ? 'approved' : 'approval revoked'}`,
          failureMessage: 'Could not set text column approval'
        })
      ))
  ));

  deleteTextComment$ = createEffect(() => this.actions$.pipe(
    ofType(deleteTextComment),
    concatMap(({ textComment }) =>
      this.api.deleteTextColumnComment(textComment.uuid).pipe(
        this.handler.effectResponse({
          retry: deleteTextComment({ textComment }),
          success: () => deleteTextCommentSuccess({ textComment }),
          failure: deleteTextCommentFailure(),
          failureMessage: 'Could not delete text comment'
        })
      ))
  ));

  clearOnAccountChangeOrProjectChange$ = createEffect(() => this.actions$.pipe(
    ofType(programmeProjectSelectionUnapplied, ProjectActions.selectSuccess),
    map(() => deselectAllResults()),
  ));

  reprocessCalculation$ = createEffect(() => this.actions$.pipe(
    ofType(reprocessCalculation),
    switchMap(({ uuid }) => this.api.reprocessCalculation(uuid).pipe(
      this.handler.effectResponse<void, void, void>({
        retry: reprocessCalculation({ uuid }),
        success: () => { /* empty */ },
        failure: () => { /* empty */ },
        failureMessage: 'Could not reprocess calculation',
      })
    ))
  ), { dispatch: false });
}
